import React, { useState, useEffect } from 'react';
import dma from '../../data/google-dma-topojson.json';
import { makeMSRequest, formatNumber, sortDmas } from '../helpers';
import DmaSelectorMap from './DmaSelectorMap';
import MapLoading from '../MapLoading';
import DownloadTheDataButton from '../DownloadTheDataButton';

const DmaExplorer = () => {
  const [dmaData, setDmaData] = useState([]);
  // set initial state from local storage if it is set
  const initialDma = localStorage.getItem('selectedDma') || '';
  const [selectedDma, setSelectedDma] = useState(initialDma);
  const [loading, setLoading] = useState(false);

  const fetchDMAData = (dma) => {
    if (dmaData.length && dmaData[0][2] === dma) {
      return;
    }

    setLoading(true);
    setSelectedDma(dma);

    const data = { dma };

    makeMSRequest(`/wp-json/ms/v1/searchByDma`, 'POST', data)
      .then((response) => {
        setDmaData(response.data);
      })
      .catch((error) => {
        console.error('Error:', error);
      })
      .finally(() => {
        setLoading(false);
      });
  };

  useEffect(() => {
    if (selectedDma.length) {
      localStorage.setItem('selectedDma', selectedDma);
      fetchDMAData(selectedDma);
    }
  }, [selectedDma]);

  const handleDMASelectChange = (dma) => {
    window.dataLayer = window.dataLayer || [];
    window.dataLayer.push({
      event: 'mediaScopeDMASelectBoxSelect',
      dma: dma
    });
    setSelectedDma(dma);
  };

  const handleDownload = () => {
    window.dataLayer = window.dataLayer || [];
    window.dataLayer.push({
      event: 'mediaScopeDataDownload',
      eventCategory: 'DmaExplorer',
      publication: selectedDma
    });

    const csv = [
      ['Publication', 'Readership'],
      ...dmaData
        .filter((d) => d['Reach'] > 0)
        .map((d) => ['"' + d['Publication'] + '"', d['Reach']])
    ]
      .map((row) => row.join(','))
      .join('\n');
    const blob = new Blob([csv], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.setAttribute('hidden', '');
    a.setAttribute('href', url);
    a.setAttribute('download', selectedDma + '.csv');
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
  };

  return (
    <div className="u--background-color-white u--padding-top-30 u--padding-bottom-30">
      <div className="media-explorer--header u--text-align-center u--padding-top-30 u--padding-bottom-30">
        <h2 className="h3">Analyze media consumption by region.</h2>
        <p>Select a DMA to view the top 15 news publications in each region.</p>
      </div>
      <div className="container container--xl">
        <div className="grid">
          <div className="grid__col-xs-12">
            <div className="information-card">
              <div className="container container--sm">
                <DmaSelectorMap
                  selectedDma={selectedDma}
                  setSelectedDma={setSelectedDma}
                />
              </div>
            </div>
          </div>
          <div className="grid__col-xs-12 grid__col-lg-6">
            <div className="information-card">
              <h3 className="h4">
                Select a DMA to see it's top news sources and their reach
                distribution
              </h3>
              <select
                name="dma"
                id="dma"
                onChange={(e) => handleDMASelectChange(e.target.value)}
                value={selectedDma}
              >
                <option value="">Select a DMA</option>
                {dma.objects.dma_shp.geometries
                  .filter((d) => d.properties.dma_name !== 'Alaska')
                  .map((d) => d.properties.dma_name)
                  .sort((a, b) => {
                    // Split the DMA name into city and state
                    const stateA = a.split(' ');
                    const stateB = b.split(' ');

                    // Extract state abbreviations
                    const sa = stateA[stateA.length - 1];
                    const sb = stateB[stateB.length - 1];

                    // Extract DMA names
                    const da = stateA[0];
                    const db = stateB[0];

                    // Compare states first
                    if (sa < sb) return -1;
                    if (sa > sb) return 1;

                    // If states are the same, compare the DMAs
                    if (da < db) return -1;
                    if (da > db) return 1;

                    return 0;
                  })
                  .map((option) => (
                    <option key={option} value={option}>
                      {option}
                    </option>
                  ))}
              </select>
              {loading ? (
                <MapLoading />
              ) : (
                <>
                  <div className="ms-map--dma-data u--margin-top-20">
                    <p className="ms-map--dma-data__header">
                      <span>DMA Stats</span>
                    </p>
                    <p>
                      <strong>Total Population</strong>
                      <span>
                        {dmaData && formatNumber(dmaData[0]?.Population)}
                      </span>
                    </p>
                    <p>
                      <strong>Adults</strong>
                      <span>
                        {dmaData &&
                          formatNumber(dmaData[0]?.['Population Over 18'])}
                      </span>
                    </p>
                    <p>
                      <strong>Median Age</strong>
                      <span>
                        {dmaData && formatNumber(dmaData[0]?.median_median_age)}
                      </span>
                    </p>
                    <p>
                      <strong>Median Household Income</strong>
                      <span>
                        {dmaData && formatNumber(dmaData[0]?.median_median_hhi)}
                      </span>
                    </p>
                    <p>
                      <strong>Counties</strong>
                      <span>{dmaData && formatNumber(dmaData[0]?.count)}</span>
                    </p>
                  </div>
                  <div className="ms-map--dma-data information-card u--padding-0 u--margin-top-20">
                    <p className="ms-map--dma-data__header">
                      <strong>Want to see the data?</strong>
                    </p>
                    <p className="u--flex-direction-column">
                      Download the complete data to explore all DMAs in this
                      news source.
                      <DownloadTheDataButton handleDownload={handleDownload} />
                    </p>
                  </div>
                </>
              )}
            </div>
          </div>
          <div className="grid__col-xs-12 grid__col-lg-6">
            <div className="information-card">
              <h3 className="h4"> News Sources</h3>
              <p>The top 15 news sources for {selectedDma}</p>
              {loading ? (
                <MapLoading />
              ) : (
                <>
                  {dmaData
                    .sort(
                      (a, b) =>
                        (b['Reach'] || 0) -
                        (a['Reach'] || 0)
                    )
                    .slice(0, 15)
                    .map((dma) => (
                      <p className="dma-news-source-data" key={dma.Publication}>
                        <strong>{dma.Publication}</strong>
                        <span>{formatNumber(dma['Reach'])}</span>
                      </p>
                    ))}
                </>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default DmaExplorer;
